<script setup lang="ts">
import EmblaCarousel, {
  type EmblaOptionsType,
  type EmblaCarouselType,
} from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import Fade from 'embla-carousel-fade';

import { type ISliderProps } from '../model/slider';
import ArrowRightCircle from '@/shared/ui/Icons/ArrowRightCircle.vue';

const { t } = useI18n();

const props = withDefaults(defineProps<ISliderProps>(), {
  autoplay: false,
  isShowPagination: false,
  isShowNavigation: false,
  effect: 'slide',
  loop: false,
  duration: 30,
  align: 'center',
  dragFree: false,
  containScroll: 'trimSnaps',
  slidesToScroll: 1,
  skipSnaps: false,
  startIndex: 0,
});

const emblaApi: Ref<EmblaCarouselType | null> = ref(null);
const emblaRef: Ref<HTMLElement | null> = ref(null);
const viewportNode: Ref<HTMLDivElement | null | undefined> = ref(null);
const prevBtnNode: Ref<HTMLButtonElement | null | undefined> = ref(null);
const nextBtnNode: Ref<HTMLButtonElement | null | undefined> = ref(null);
const currentIndex: Ref<number> | undefined = ref(props.startIndex || 0);

const OPTIONS: EmblaOptionsType = {
  loop: props.loop,
  duration: props.duration,
  dragFree: props.dragFree,
  containScroll: props.containScroll,
  align: props.align,
  skipSnaps: props.skipSnaps,
  slidesToScroll: props.slidesToScroll,
  startIndex: props.startIndex,
};

const slideSize = computed(() => {
  return 100 / (props.itemsToShow || 1) + '%';
});

const slideSpacing = computed(() => {
  return `${props.gap || 0}px`;
});

const handleDotClick = (index: number) => {
  emblaApi.value?.scrollTo(index);
  currentIndex.value = index;
};

const onSlideChange = () => {
  currentIndex.value = emblaApi.value?.selectedScrollSnap() || 0;
};

const addTogglePrevNextBtnsActive = (
  emblaApi: EmblaCarouselType | null | undefined,
  prevBtn: HTMLButtonElement | null | undefined,
  nextBtn: HTMLButtonElement | null | undefined,
): (() => void) => {
  if (!emblaApi || !prevBtn || !nextBtn) return () => {};
  const togglePrevNextBtnsState = (): void => {
    if (emblaApi?.canScrollPrev()) prevBtn?.removeAttribute('disabled');
    else prevBtn.setAttribute('disabled', 'disabled');

    if (emblaApi.canScrollNext()) nextBtn.removeAttribute('disabled');
    else nextBtn.setAttribute('disabled', 'disabled');
  };

  emblaApi
    .on('select', togglePrevNextBtnsState)
    .on('init', togglePrevNextBtnsState)
    .on('reInit', togglePrevNextBtnsState);

  return (): void => {
    prevBtn.removeAttribute('disabled');
    nextBtn.removeAttribute('disabled');
  };
};

const addPrevNextBtnsClickHandlers = (
  emblaApi: EmblaCarouselType | null | undefined,
  prevBtn: HTMLButtonElement | null | undefined,
  nextBtn: HTMLButtonElement | null | undefined,
): (() => void) => {
  if (!emblaApi || !prevBtn || !nextBtn) return () => {};

  const scrollPrev = (): void => {
    emblaApi.scrollPrev();
  };
  const scrollNext = (): void => {
    emblaApi.scrollNext();
  };
  prevBtn.addEventListener('click', scrollPrev, false);
  nextBtn.addEventListener('click', scrollNext, false);

  const removeTogglePrevNextBtnsActive = addTogglePrevNextBtnsActive(
    emblaApi,
    prevBtn,
    nextBtn,
  );

  return (): void => {
    removeTogglePrevNextBtnsActive();
    prevBtn.removeEventListener('click', scrollPrev, false);
    nextBtn.removeEventListener('click', scrollNext, false);
  };
};

const getPlugins = async (): Promise<any[]> => {
  const plugins = [];
  if (props.autoplay)
    plugins.push(Autoplay({ playOnInit: props.autoplay, stopOnInteraction: true }));
  if (props.effect === 'fade') plugins.push(Fade());
  return plugins;
};

onMounted(async () => {
  if (viewportNode.value) {
    const plugins = await getPlugins();
    emblaApi.value = EmblaCarousel(viewportNode.value, OPTIONS, [...plugins]);
    emblaApi.value.on('select', onSlideChange);
  }

  if (prevBtnNode.value && nextBtnNode.value) {
    const removePrevNextBtnsClickHandlers = addPrevNextBtnsClickHandlers(
      emblaApi.value,
      prevBtnNode.value,
      nextBtnNode.value,
    );
    emblaApi.value?.on('destroy', removePrevNextBtnsClickHandlers);
  }
});
</script>

<template>
  <div class="embla" ref="emblaRef">
    <div class="embla__viewport" ref="viewportNode">
      <div class="embla__container">
        <div v-for="(slide, index) in slides" :key="index" class="embla__slide">
          <slot name="slide" :slide="slide" :index="index" />
        </div>
      </div>
    </div>
    <button
      v-if="isShowNavigation"
      ref="prevBtnNode"
      class="embla__button _prev"
      type="button"
      :aria-label="t('arrowBack')"
    >
      <ArrowRightCircle style="transform: rotate(180deg)" />
    </button>
    <button
      v-if="isShowNavigation"
      ref="nextBtnNode"
      class="embla__button _next"
      type="button"
      :aria-label="t('arrowForward')"
    >
      <ArrowRightCircle />
    </button>
    <div v-if="isShowPagination" class="embla__dots">
      <button
        v-for="(_, index) in slides"
        :key="index"
        :class="['embla__dot', { _selected: currentIndex === index }]"
        type="button"
        :aria-label="`${t('goToSlide')} ${index + 1}`"
        @click="handleDotClick(index)"
      ></button>
    </div>
  </div>
</template>

<style lang="less" scoped>
.embla {
  margin: auto;
  --slide-spacing: v-bind(slideSpacing);
  --slide-size: v-bind(slideSize);

  &__viewport {
    overflow: hidden;
  }

  &__container {
    display: flex;
    touch-action: pan-y pinch-zoom;
    margin-left: calc(var(--slide-spacing) * -1);
  }

  &__slide {
    transform: translate3d(0, 0, 0);
    flex: 0 0 var(--slide-size);
    min-width: 0;
    padding-left: var(--slide-spacing);
  }

  &__button {
    .button-reset();

    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
    width: 44px;
    height: 44px;
    background: rgba(248, 248, 250, 1);
    border-radius: 100%;
    display: flex;
    justify-content: center;

    &._prev {
      left: 44px;
    }

    &._next {
      right: 44px;
    }

    &[disabled] {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  &__dots {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: center;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: 12px;
    gap: 6px;
  }

  &__dot {
    .button-reset();

    width: 8px;
    height: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 100%;
    background-color: rgba(255, 255, 255, 0.8);

    &._selected {
      width: 12px;
      height: 12px;
      background-color: white;
    }
  }

  @media @desktop {
    &__button {
      width: @size-x9;
      height: @size-x9;

      svg {
        width: @size-x9;
        height: @size-x9;
      }
    }
  }
}
</style>
